import React from 'react';
import {
  Package, UtensilsCrossed, Users, DollarSign, Clock, CheckCircle, ChefHat, Truck,
  TrendingUp, Plus, Edit, Trash2, GripVertical, ArrowUp, ArrowDown, FolderOpen,
  Upload, Image, Star, MessageSquare, Mail, Eye, Reply, Send, Megaphone, Tag, Calendar, X, ChevronLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { apiRequest, formatPrice, getAuthToken } from '../../lib/utils';

const CategoriesTab = ({
  categories,
  categoryForm,
  categoryImageRef,
  deleteCategory,
  editingCategory,
  handleCategoryImageUpload,
  handleCategorySubmit,
  isCategoryDialogOpen,
  menuItems,
  moveCategoryDown,
  moveCategoryUp,
  openEditCategoryDialog,
  resetCategoryForm,
  setCategoryForm,
  setIsCategoryDialogOpen,
  uploadingCategoryImage,
}) => (
          <TabsContent value="categories">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Categories</h2>
              <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-[#FFC107] text-black hover:bg-[#F59E0B]"
                    onClick={resetCategoryForm}
                    data-testid="add-category"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#141417] border-[#27272A] max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCategorySubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Category Name</Label>
                      <Input
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                        required
                        placeholder="e.g., Pizza, Burgers, Ethnic Food"
                        className="bg-[#0B0B0C] border-[#27272A]"
                        data-testid="category-form-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={categoryForm.description}
                        onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                        placeholder="Brief description of the category"
                        className="bg-[#0B0B0C] border-[#27272A]"
                        data-testid="category-form-description"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Image</Label>
                      <input
                        type="file"
                        ref={categoryImageRef}
                        onChange={handleCategoryImageUpload}
                        accept="image/*"
                        className="hidden"
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => categoryImageRef.current?.click()}
                          disabled={uploadingCategoryImage}
                          className="flex-1 border-zinc-700 hover:bg-[#25252E]"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingCategoryImage ? 'Uploading...' : 'Upload Image'}
                        </Button>
                      </div>
                      {categoryForm.image && (
                        <div className="mt-2">
                          <img 
                            src={categoryForm.image} 
                            alt="Preview" 
                            className="w-full h-32 object-cover rounded-lg"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Display Order</Label>
                      <Input
                        type="number"
                        value={categoryForm.order}
                        onChange={(e) => setCategoryForm({...categoryForm, order: e.target.value})}
                        className="bg-[#0B0B0C] border-[#27272A]"
                        data-testid="category-form-order"
                      />
                      <p className="text-xs text-zinc-500">Lower numbers appear first</p>
                    </div>
                    <Button type="submit" className="w-full bg-[#FFC107] text-black hover:bg-[#F59E0B]" data-testid="category-form-submit">
                      {editingCategory ? 'Update Category' : 'Add Category'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <Card className="bg-[#141417] border-[#27272A]">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#27272A]">
                      <TableHead className="w-12">Order</TableHead>
                      <TableHead>Image</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((category, index) => (
                      <TableRow key={category.id} className="border-[#27272A]" data-testid={`category-row-${category.id}`}>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => moveCategoryUp(category)}
                              disabled={index === 0}
                              data-testid={`move-up-${category.id}`}
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <span className="text-center text-sm text-zinc-400">{category.order}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => moveCategoryDown(category)}
                              disabled={index === categories.length - 1}
                              data-testid={`move-down-${category.id}`}
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                            {category.image ? (
                              <img src={category.image} alt={category.name_en || category.name} className="w-20 h-16 object-cover rounded-lg" />
                            ) : (
                              <div className="w-20 h-16 rounded-lg bg-[#1C1C22] flex items-center justify-center text-zinc-500 text-xs">No img</div>
                            )}
                        </TableCell>
                        <TableCell className="font-semibold">{category.name_en || category.name}</TableCell>
                        <TableCell className="text-zinc-400 text-sm max-w-xs truncate">
                          {category.description_en || category.description || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-zinc-700">
                            {menuItems.filter(item => item.category_id === category.id).length} items
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => openEditCategoryDialog(category)}
                              data-testid={`edit-category-${category.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-red-400 hover:text-red-300"
                              onClick={() => deleteCategory(category.id)}
                              data-testid={`delete-category-${category.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
);

export default CategoriesTab;
